import { Button } from "@/components/ui/button";
import { Check, MessageSquare, Bell, Eye, Star, Sparkles, Loader2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Order } from "@/hooks/useOrders";
import { NotificationSettings } from "@/hooks/useNotificationSettings";
import { getOrderActionModel, OrderActionKey } from "@/utils/orderActions";

interface OrderActionsProps {
  order: Order;
  onSendConfirmation: (order: Order) => void | Promise<void>;
  onSendReadyNotification: (order: Order) => void | Promise<void>;
  onViewOffer: (order: Order) => void;
  onSendReviewRequest: (order: Order) => void | Promise<void>;
  notificationSettings?: NotificationSettings | null;
  /**
   * Whether that send is already in flight for this order. Required, not
   * optional: the guard has to be owned above the rows to survive a remount, so
   * a call site that cannot answer this cannot safely render a send button.
   */
  isSending: (action: OrderActionKey) => boolean;
  /** "full" for the list OrderCard, "compact" for the dense By-Time rows. */
  variant?: "full" | "compact";
}

interface StepMeta {
  icon: LucideIcon;
  /** Active call to action (the one thing to do now). */
  primaryLabel: string;
  /** Quieter label when the step is available but not the current step. */
  secondaryLabel: string;
  /** Past-tense chip once sent. */
  doneLabel: string;
  /** Outline treatment when demoted to a secondary control. */
  secondaryClass: string;
}

const STEP_META: Record<OrderActionKey, StepMeta> = {
  confirm: {
    icon: MessageSquare,
    primaryLabel: "Confirm order",
    secondaryLabel: "Confirm",
    doneLabel: "Confirmed",
    secondaryClass:
      "border-action-primary/40 text-action-primary hover:bg-action-primary/10",
  },
  ready: {
    icon: Bell,
    primaryLabel: "Mark order ready",
    secondaryLabel: "Ready",
    doneLabel: "Ready sent",
    secondaryClass:
      "border-status-warning/40 text-status-warning hover:bg-status-warning/10",
  },
  review: {
    icon: Star,
    primaryLabel: "Ask for review",
    secondaryLabel: "Review",
    doneLabel: "Review asked",
    secondaryClass:
      "border-status-info/40 text-status-info hover:bg-status-info/10",
  },
};

/**
 * The order lifecycle rendered as one confident primary action plus demoted
 * secondaries. Done steps collapse to quiet chips; "View offer" is a utility,
 * never the next step. Shared by OrderCard and TimeGroupedOrdersView so the
 * button vocabulary is identical across views.
 */
export function OrderActions({
  order,
  onSendConfirmation,
  onSendReadyNotification,
  onViewOffer,
  onSendReviewRequest,
  notificationSettings,
  isSending,
  variant = "full",
}: OrderActionsProps) {
  const model = getOrderActionModel(order, {
    autoConfirmEnabled: notificationSettings?.auto_confirm_enabled,
  });

  const handlers: Record<OrderActionKey, (order: Order) => void | Promise<void>> = {
    confirm: onSendConfirmation,
    ready: onSendReadyNotification,
    review: onSendReviewRequest,
  };

  const compact = variant === "compact";
  const hasOffer = !!order.offer_id;

  const primaryKey = model.primaryKey;
  const secondaryKeys = model.order.filter(
    (k) => k !== primaryKey && model.steps[k].actionable,
  );
  // Only surface a note for the non-obvious confirm blockers (auto-confirm,
  // needs an offer). "No phone number" is already shown in the card header.
  const confirmStep = model.steps.confirm;
  const blockedNote =
    !confirmStep.done && !confirmStep.actionable ? confirmStep.blockedReason : undefined;

  const primaryHeight = compact ? "h-11" : "h-12 sm:h-11";
  const secondaryHeight = "h-11";

  // A send is a real SMS: one thumb tap must not become two while the round trip
  // is in flight. The gate itself lives with whoever owns the send (see
  // useSendGuard) so it survives this component unmounting mid-send; here it
  // only decides what is tappable and which control wears the sending cue.
  // While any send is out, every sending control on the row is closed.
  const sending = model.order.some((k) => isSending(k));

  const runSend = (key: OrderActionKey) => {
    if (sending) return;
    void handlers[key](order);
  };

  const primaryInFlight = primaryKey !== null && isSending(primaryKey);
  const PrimaryIcon = primaryKey
    ? primaryInFlight
      ? Loader2
      : STEP_META[primaryKey].icon
    : null;

  const showChips = model.doneKeys.length > 0 || !!blockedNote;
  const showControls = !!primaryKey || secondaryKeys.length > 0 || hasOffer;
  const showResting =
    !primaryKey && secondaryKeys.length === 0 && !blockedNote && model.doneKeys.length > 0;

  return (
    <div className={compact ? "space-y-2.5" : "space-y-3"}>
      {/* Done + status chips: what's already been handled, at a glance. */}
      {showChips && (
        <div className="flex flex-wrap items-center gap-1.5">
          {model.doneKeys.map((k) => (
            <span
              key={k}
              className="inline-flex items-center gap-1 rounded-full bg-status-success/15 px-2.5 py-1 text-xs font-medium text-status-success"
            >
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              {STEP_META[k].doneLabel}
            </span>
          ))}
          {blockedNote && (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
              {blockedNote}
            </span>
          )}
        </div>
      )}

      {/* Full-width thumb targets on a phone; a compact inline bar on wider
          screens so the brand red never becomes a page-wide slab. */}
      {showControls && (
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          {primaryKey && PrimaryIcon && (
            <Button
              onClick={() => runSend(primaryKey)}
              disabled={sending}
              className={`w-full sm:w-auto sm:min-w-[11rem] ${primaryHeight} gap-2 bg-action-primary text-action-primary-foreground hover:opacity-90 text-base font-semibold`}
            >
              <PrimaryIcon
                className={`h-5 w-5 ${primaryInFlight ? "animate-spin" : ""}`}
                aria-hidden="true"
              />
              <span>{primaryInFlight ? "Sending" : STEP_META[primaryKey].primaryLabel}</span>
            </Button>
          )}

          {(secondaryKeys.length > 0 || hasOffer) && (
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
              {secondaryKeys.map((k) => {
                const inFlight = isSending(k);
                const Icon = inFlight ? Loader2 : STEP_META[k].icon;
                return (
                  <Button
                    key={k}
                    onClick={() => runSend(k)}
                    disabled={sending}
                    variant="outline"
                    className={`w-full sm:w-auto ${secondaryHeight} gap-2 text-sm font-medium ${STEP_META[k].secondaryClass}`}
                  >
                    <Icon className={`h-4 w-4 ${inFlight ? "animate-spin" : ""}`} aria-hidden="true" />
                    <span>{inFlight ? "Sending" : STEP_META[k].secondaryLabel}</span>
                  </Button>
                );
              })}
              {hasOffer && (
                <Button
                  onClick={() => onViewOffer(order)}
                  variant="outline"
                  className={`w-full sm:w-auto ${secondaryHeight} gap-2 text-sm font-medium border-border text-muted-foreground hover:bg-muted hover:text-foreground`}
                >
                  <Eye className="h-4 w-4" aria-hidden="true" />
                  <span>View offer</span>
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Nothing left to do: a calm resting state, never a broken-looking gap.
          Suppressed while a step is blocked or a send is still on offer, so the
          card never claims completion next to an unsent notification. */}
      {showResting && (
        <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-status-success" aria-hidden="true" />
          All notifications sent
        </p>
      )}

      {/* Nothing at all applies to this order (e.g. a dine-in with no phone and
          no offer): say so quietly rather than leaving a blank padded box. */}
      {!showChips && !showControls && (
        <p className="text-sm text-muted-foreground">No actions for this order</p>
      )}
    </div>
  );
}

export default OrderActions;
